import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';

interface GoldysSummary {
  party_count: number;
  gross_sales: number;
  status: string;
}

function todayJst() {
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
}

export default function GoldysPage() {
  const { profile } = useAuth();
  const canApprove = profile?.access_level === 'super_admin' || profile?.access_level === 'operations_manager';
  const [summary, setSummary] = useState<GoldysSummary | null>(null);

  useEffect(() => {
    supabase.rpc('get_goldys_daily_summary', { p_date: todayJst() }).then(({ data, error }) => {
      if (!error) setSummary(data as GoldysSummary);
    });
  }, []);

  const actions = [
    { to: '/goldys-closing', icon: 'ri-cash-line', label: '本日の営業・日次締め', note: '組数、Square売上、現金在高と締め作業', color: 'bg-amber-50 text-amber-800' },
    { to: '/purchase-requests', icon: 'ri-shopping-bag-3-line', label: '発注・納品管理', note: '必要品、希望日、手配担当と納品予定', color: 'bg-blue-50 text-blue-800' },
    { to: '/reimbursements', icon: 'ri-receipt-line', label: '立替・レシート', note: '写真を登録して精算と損益へ反映', color: 'bg-violet-50 text-violet-800' },
    { to: '/messages', icon: 'ri-chat-3-line', label: '店舗メッセージ', note: '連絡事項と引継ぎを確認', color: 'bg-sky-50 text-sky-800' },
    { to: '/shifts', icon: 'ri-calendar-schedule-line', label: '店舗シフト', note: '公開済みシフトと勤務担当を確認', color: 'bg-rose-50 text-rose-800' },
    ...(canApprove ? [{ to: '/attendance/admin', icon: 'ri-user-follow-line', label: '勤怠承認', note: '打刻差異と未承認の勤務を確認', color: 'bg-primary-50 text-primary-800' }] : []),
  ];

  return <div className="mx-auto max-w-6xl space-y-5">
    <header className="rounded-2xl bg-[#4D351F] px-5 py-5 text-white md:px-7">
      <p className="text-xs font-bold uppercase tracking-[.18em] text-amber-200">Goldy’s Operations</p>
      <div className="mt-2 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div><h1 className="text-2xl font-extrabold">Goldy’s 店舗業務</h1><p className="mt-1 text-sm text-amber-50/80">今日の営業、支払、発注、スタッフ業務をここから確認します。</p></div>
        <div className="flex gap-5 rounded-xl bg-white/10 px-4 py-3 text-sm backdrop-blur-sm">
          <div><p className="text-xs text-amber-100/70">本日の組数</p><strong className="text-xl">{summary?.party_count || 0}<span className="ml-1 text-xs">組</span></strong></div>
          <div><p className="text-xs text-amber-100/70">本日の売上</p><strong className="text-xl">¥{(summary?.gross_sales || 0).toLocaleString()}</strong></div>
          <div><p className="text-xs text-amber-100/70">締め状況</p><strong className="text-sm">{summary?.status === 'closed' ? '締め済み' : '未締め'}</strong></div>
        </div>
      </div>
    </header>

    <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {actions.map((action) => <Link key={action.to} to={action.to} className="flex min-h-[112px] items-center gap-4 rounded-xl border border-background-200 bg-white p-4 transition hover:-translate-y-0.5 hover:shadow-md">
        <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${action.color}`}><i className={`${action.icon} text-xl`} /></span>
        <span className="min-w-0"><strong className="block text-sm text-foreground-900">{action.label}</strong><span className="mt-1 block text-xs leading-relaxed text-foreground-500">{action.note}</span></span>
        <i className="ri-arrow-right-s-line ml-auto text-foreground-400" />
      </Link>)}
    </section>

    <section className="flex flex-col gap-2 rounded-xl border border-background-200 bg-background-50 px-4 py-3 text-xs text-foreground-500 sm:flex-row sm:items-center sm:justify-between">
      <span><i className="ri-information-line mr-2" />必要な場合のみ、給与・業務委託の日払いを処理できます。</span>
      <Link to="/daily-pay" className="font-semibold text-foreground-600 hover:text-primary-700">その他の手続き：日払い精算 <i className="ri-arrow-right-s-line" /></Link>
    </section>
  </div>;
}
